"use client";

import Link from "next/link";
import { useEffect, useLayoutEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { portalLoginHref } from "@/lib/portal-public-url";

const LANG_SCROLL_KEY = "flcare:langSwitchScrollY";
/** Volle URL inkl. Hash nach Navigation per replaceState (Router ohne Hash, kein Anker-Sprung). */
const LANG_URL_KEY = "flcare:langSwitchUrl";

function getLang(searchParams: ReturnType<typeof useSearchParams>) {
  const lang = searchParams.get("lang");
  return lang === "de" ? "de" : "en";
}

export function SiteHeader() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lang = getLang(searchParams);
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  function switchLanguage(targetLang: "de" | "en") {
    if (lang === targetLang) return;

    const params = new URLSearchParams(searchParams.toString());
    params.set("lang", targetLang);
    const pathQuery = `${pathname}?${params.toString()}`;

    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const withHash = `${pathQuery}${hash}`;

    try {
      sessionStorage.setItem(LANG_SCROLL_KEY, String(window.scrollY));
      if (hash) {
        sessionStorage.setItem(LANG_URL_KEY, withHash);
      } else {
        sessionStorage.removeItem(LANG_URL_KEY);
      }
    } catch {
      /* ignore */
    }
    router.replace(hash ? pathQuery : withHash, { scroll: false });
  }

  useLayoutEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(LANG_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null) return;
    try {
      sessionStorage.removeItem(LANG_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = Number(raw);
    if (!Number.isFinite(y)) return;

    let urlAfter: string | null = null;
    try {
      urlAfter = sessionStorage.getItem(LANG_URL_KEY);
      if (urlAfter != null) sessionStorage.removeItem(LANG_URL_KEY);
    } catch {
      /* ignore */
    }

    const restore = () => {
      const opts = { top: y, left: 0, behavior: "auto" as const };
      window.scrollTo(opts);
      document.documentElement.scrollTop = y;
      document.body.scrollTop = y;
    };
    restore();
    if (urlAfter) {
      try {
        window.history.replaceState(window.history.state, "", urlAfter);
      } catch {
        /* ignore */
      }
      restore();
    }
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
    const t0 = setTimeout(restore, 0);
    const t1 = setTimeout(restore, 50);
    const t2 = setTimeout(restore, 200);
    const t3 = setTimeout(restore, 500);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [searchParams]);

  const publicNav =
    lang === "de"
      ? [
          { href: "/?lang=de", label: "Home" },
          { href: "/technology?lang=de", label: "Technology" },
          { href: "/?lang=de#industries", label: "Industries" },
          { href: "/?lang=de#contact", label: "Contact" },
        ]
      : [
          { href: "/?lang=en", label: "Home" },
          { href: "/technology?lang=en", label: "Technology" },
          { href: "/?lang=en#industries", label: "Industries" },
          { href: "/?lang=en#contact", label: "Contact" },
        ];

  return (
    <header className="site-header site-header--public">
      <div className="site-header__inner">
        <div className="brand-wrap">
          <Link href={`/?lang=${lang}`} className="brand">
            FL-Care
          </Link>
        </div>

        <nav className="site-nav">
          {publicNav.map((item) => (
            <Link key={item.href} href={item.href} className="site-nav__link">
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <div className="lang-switch">
            <button
              type="button"
              className={`lang-switch__item ${lang === "de" ? "lang-switch__item--active" : ""}`}
              aria-current={lang === "de" ? "true" : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => switchLanguage("de")}
            >
              DE
            </button>
            <button
              type="button"
              className={`lang-switch__item ${lang === "en" ? "lang-switch__item--active" : ""}`}
              aria-current={lang === "en" ? "true" : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => switchLanguage("en")}
            >
              EN
            </button>
          </div>

          <a href={portalLoginHref(lang)} className="button button--primary">
            Login
          </a>
        </div>
      </div>
    </header>
  );
}
